/**
 * Passkeys (the better-auth passkey client, loaded on demand so the map never pays for it).
 * Sign-in with email + password lives in `auth-api.ts`. Fixtures mode answers from memory.
 */
import type { AuthFailure } from "./auth-api.ts";

export interface PasskeyInfo {
  id: string;
  name: string | null;
  createdAt: string | null;
}

const failure = (status: number, text: string): AuthFailure => {
  if (status === 429) return "rate_limited";
  if (/banned/i.test(text)) return "banned";
  return "failed";
};

async function client() {
  const [{ createAuthClient }, { passkeyClient }] = await Promise.all([
    import("better-auth/client"),
    import("@better-auth/passkey/client"),
  ]);
  return createAuthClient({ basePath: "/api/auth", plugins: [passkeyClient()] });
}

export const passkeysSupported = (): boolean =>
  typeof window !== "undefined" && "PublicKeyCredential" in window;

export async function signInWithPasskey(): Promise<{ ok: boolean; reason?: AuthFailure }> {
  if (import.meta.env.VITE_API_MODE !== "live")
    return (await import("./fixture-auth.ts")).signInWithPasskey();
  const res = await (await client()).signIn.passkey();
  if (res?.error) {
    return { ok: false, reason: failure(res.error.status ?? 0, res.error.message ?? "") };
  }
  return { ok: true };
}

export async function addPasskey(name: string): Promise<{ ok: boolean; cancelled?: boolean }> {
  if (import.meta.env.VITE_API_MODE !== "live")
    return (await import("./fixture-auth.ts")).addPasskey(name);
  const res = await (await client()).passkey.addPasskey({ name });
  if (res?.error) {
    const code = String((res.error as { code?: string }).code ?? "");
    return { ok: false, cancelled: /CANCEL|ABORT|NOT_ALLOWED/i.test(code) };
  }
  return { ok: true };
}

export async function listPasskeys(): Promise<PasskeyInfo[]> {
  if (import.meta.env.VITE_API_MODE !== "live")
    return (await import("./fixture-auth.ts")).listPasskeys();
  const res = await (await client()).passkey.listUserPasskeys();
  const rows = (res?.data ?? []) as {
    id: string;
    name?: string | null;
    createdAt?: string | Date | null;
  }[];
  return rows.map((p) => ({
    id: p.id,
    name: p.name ?? null,
    createdAt: p.createdAt ? new Date(p.createdAt).toISOString() : null,
  }));
}

export async function deletePasskey(id: string): Promise<boolean> {
  if (import.meta.env.VITE_API_MODE !== "live")
    return (await import("./fixture-auth.ts")).deletePasskey(id);
  const res = await (await client()).passkey.deletePasskey({ id });
  return !res?.error;
}
